/**
 * @module
 * @format
 */

/**
 * @import {SubmitAction} from 'nova';
 * @import {Selection} from '@cropper/element-selection';
 */

import {FormController, location} from 'nova';
import {query} from '@acng/frontend-bounty/dom/query.js';
import {guard, IS_HTML} from '@acng/frontend-rubicon';
import {make, trackOncePerMinute, UserProfileImageApi} from './api.js';
import {Size} from '@acng/frontend-bounty/dom/observe.js';
import {createPromise} from '@acng/frontend-bounty/std/control.js';
import {CHANGE, on} from '@acng/frontend-bounty/dom/event.js';
import {UserImageWaiting} from './waiting.js';

class UserImageCrop extends FormController {
  /**
   * @param {Selection} selection
   * @param {Selection} maxSelection
   */
  static #inSelection(selection, maxSelection) {
    return (
      selection.x >= maxSelection.x &&
      selection.y >= maxSelection.y &&
      selection.x + selection.width <= maxSelection.x + maxSelection.width &&
      selection.y + selection.height <= maxSelection.y + maxSelection.height
    );
  }

  /** @type {string | undefined} */
  image;

  #returnHref = '/';

  /**
   * @param {File | undefined} file
   * @returns {void}
   */
  set(file) {
    if (!file) {
      this.image = undefined;

      return;
    }

    this.image = URL.createObjectURL(file);
    this.#returnHref = location.pathname;
    location.href = `/profile/image-crop`;
  }

  #leave() {
    location.replace(this.#returnHref);
  }

  /**
   * Lazy load cropperjs.
   */
  async compile() {
    await import('cropperjs');
  }

  /** @param {HTMLFormElement} form */
  upgrade(form) {
    let valid = false;
    /** @type {DOMRect | undefined} */
    let cropperCanvasRect;
    /** @type {DOMRect | undefined} */
    let cropperImageRect;
    const cropperCanvas = query(form, 'cropper-canvas');
    const cropperImage = query(form, 'cropper-image');
    const cropperSelection = query(form, 'cropper-selection');

    ASSERT: {
      guard(cropperCanvas, IS_HTML);
      guard(cropperImage, IS_HTML);
      guard(cropperSelection, IS_HTML);
    }

    Size(() => {
      cropperImage.$center('contain');
      cropperSelection.$center();
      cropperCanvasRect = cropperCanvas.getBoundingClientRect();
      valid = false;
    })(cropperCanvas);

    Size(() => {
      cropperImageRect = cropperImage.getBoundingClientRect();
      valid = false;
    })(cropperImage);

    on(cropperSelection, CHANGE, (evt) => {
      if (!cropperImageRect || !cropperCanvasRect) {
        return;
      }

      // @ts-ignore
      const selection = /** @type {Selection} */ (evt.detail);

      const maxSelection = {
        x: cropperImageRect.left - cropperCanvasRect.left,
        y: cropperImageRect.top - cropperCanvasRect.top,
        width: cropperImageRect.width,
        height: cropperImageRect.height,
      };

      const ok = UserImageCrop.#inSelection(selection, maxSelection);

      if (!ok) {
        if (valid) {
          evt.preventDefault();
        }
      } else {
        valid = ok;
      }
    });

    on(form, 'reset', () => this.#leave());
  }

  /**
   * @type {SubmitAction}
   */
  async submit(form, data) {
    const cropperSelection = query(form, 'cropper-selection');
    ASSERT: guard(cropperSelection, IS_HTML);

    cropperSelection.hidden = true;

    const options = {width: 800, height: 800};
    const canvas = await cropperSelection.$toCanvas(options);

    const blob = await createPromise((resolve) =>
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            throw this;
          }

          resolve(blob);
        },
        'image/jpeg',
        0.9,
      ),
    );

    data.append('image', blob);

    await make(UserProfileImageApi)
      .post(data, form)
      .catch((e) => this.reason.error('Upload failed.', e));

    await make(UserImageWaiting)
      .sync()
      .catch((e) => this.reason.warn('Upload service update failed.', e));

    trackOncePerMinute();
    this.#leave();
  }
}

export {UserImageCrop};
